import { Building, Check, Monitor, Tag, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import InvoiceFilterButton from './invoice-filter-button';
import { Button } from 'src/components/ui/button';

export interface FilterSelections {
  skus: Array<{id: string, name: string}>;
  statuses: Array<{id: string, name: string}>;
  platforms: Array<{id: string, name: string}>;
  owners: Array<{id: string, name: string}>;
  companies: Array<{id: string, name: string}>;
}

interface InvoiceFilterProps {
  skus?: Array<{id: string, name: string}>;
  statuses?: Array<{id: string, name: string}>;
  platforms?: Array<{id: string, name: string}>;
  owners?: Array<{id: string, name: string}>;
  companies?: Array<{id: string, name: string}>;
  onSelectionChange?: (selections: FilterSelections) => void;
}

const InvoiceFilter: React.FC<InvoiceFilterProps> = ({ skus, statuses, platforms, owners, companies, onSelectionChange }) => {
  const [selectedSkus, setSelectedSkus] = useState<Array<{id: string, name: string}>>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Array<{id: string, name: string}>>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Array<{id: string, name: string}>>([]);
  const [selectedOwners, setSelectedOwners] = useState<Array<{id: string, name: string}>>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{id: string, name: string}>>([]);

  const handleClearSelections = () => {
    setSelectedSkus([]);
    setSelectedStatuses([]);
    setSelectedPlatforms([]);
    setSelectedOwners([]);
    setSelectedCompanies([]);
  };

  useEffect(() => {
    onSelectionChange?.({
      skus: selectedSkus,
      statuses: selectedStatuses,
      platforms: selectedPlatforms,
      owners: selectedOwners,
      companies: selectedCompanies,
    });
  }, [selectedSkus, selectedStatuses, selectedPlatforms, selectedOwners, selectedCompanies]);

  return (
    <div className="p-4 pt-2 bg-gray-201">
      <div className='flex flex-row gap-2 items-start mb-1'>
        <Button
          variant='outline'
          size='sm'
          onClick={handleClearSelections}
        >
          <X className="h-4 w-4" />
        </Button>
        <InvoiceFilterButton
          icon={<Tag className="h-4 w-4" />}
          title="SKU"
          options={skus || []}
          selectedOptions={selectedSkus}
          onSelectionChange={setSelectedSkus}
        />
        <InvoiceFilterButton
          icon={<Check className="h-4 w-4" />}
          title="Status"
          options={statuses ? statuses.map((status: {id: string, name: string}) => ({id: status.id, name: status.name})) : []}
          selectedOptions={selectedStatuses}
          onSelectionChange={setSelectedStatuses}
        />
        <InvoiceFilterButton
          icon={<Monitor className="h-4 w-4" />}
          title="Platform"
          options={platforms ? platforms.map((platform: {id: string, name: string}) => ({id: platform.id, name: platform.name})) : []}
          selectedOptions={selectedPlatforms}
          onSelectionChange={setSelectedPlatforms}
        />
        <InvoiceFilterButton
          icon={<User className="h-4 w-4" />}
          title="Owner"
          options={owners ? owners.map((owner: {id: string, name: string}) => ({id: owner.id, name: owner.name})) : []}
          selectedOptions={selectedOwners}
          onSelectionChange={setSelectedOwners}
        />
        <InvoiceFilterButton
          icon={<Building className="h-4 w-4" />}
          title="Company"
          options={companies ? companies.map((company: {id: string, name: string}) => ({id: company.id, name: company.name})) : []}
          selectedOptions={selectedCompanies}
          onSelectionChange={setSelectedCompanies}
        />
      </div>
      <div className='text-sm font-medium text-gray-700 mt-2 border-b border-gray-200 mb-2 w-1/4'>
        {selectedSkus.length > 0 || selectedStatuses.length > 0 || selectedPlatforms.length > 0 || selectedOwners.length > 0 || selectedCompanies.length > 0 ? 'Selections' : 'No selections'}
      </div>
      <div className='flex flex-row gap-2 items-start flex-wrap'>
        {/* Pills for selected filters */}
        {selectedSkus.map((sku) => (
          <div
            key={`sku-pill-${sku.id}`}
            className="flex items-center bg-gray-100 rounded-full px-2 py-1 mr-1 text-xs font-medium text-gray-700"
          >
            <Tag className="h-4 w-4 mr-1 text-gray-500" />
            {sku.name}
          </div>
        ))}
        {selectedStatuses.map((status) => (
          <div
            key={`status-pill-${status.id}`}
            className="flex items-center bg-gray-100 rounded-full px-2 py-1 mr-1 text-xs font-medium text-gray-700"
          >
            <Check className="h-4 w-4 mr-1 text-gray-500" />
            {status.name}
          </div>
        ))}
        {selectedPlatforms.map((platform) => (
          <div
            key={`platform-pill-${platform.id}`}
            className="flex items-center bg-gray-100 rounded-full px-2 py-1 mr-1 text-xs font-medium text-gray-700"
          >
            <Monitor className="h-4 w-4 mr-1 text-gray-500" />
            {platform.name}
          </div>
        ))}
        {selectedOwners.map((owner) => (
          <div
            key={`owner-pill-${owner.id}`}
            className="flex items-center bg-gray-100 rounded-full px-2 py-1 mr-1 text-xs font-medium text-gray-700"
          >
            <User className="h-4 w-4 mr-1 text-gray-500" />
            {owner.name}
          </div>
        ))}
        {selectedCompanies.map((company) => (
          <div
            key={`company-pill-${company.id}`}
            className="flex items-center bg-gray-100 rounded-full px-2 py-1 mr-1 text-xs font-medium text-gray-700"
          >
            <Building className="h-4 w-4 mr-1 text-gray-500" />
            {company.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvoiceFilter;
